import { useEffect, useLayoutEffect, useState } from "react"

/* Which layout the window can hold.

   The three names are layouts, not devices. Nothing here asks what a device is — a laptop window
   dragged narrow has a phone's problem and a phone in landscape has a tablet's, and a user-agent
   test gets both of those backwards. The only question is how many pixels are left for the map
   after the chrome, and that is arithmetic:

     desktop   two fixed docks, 340 + 350 (theme.css floors both clamps), map = the remainder
     tablet    a 56px icon rail whose panels open over the map, and one 280px dock
     phone     no docks at all; the map is the floor and every panel is a sheet over it

   A cell is 24px at the default zoom, so "how much map" is really "how many cells". Twelve is the
   floor a window has to clear to be worth showing at all — it is the number MobileGate is set by.
   Seventeen is where a map stops feeling like a porthole, and it is the better bar for *choosing*
   between two layouts, because the layout below always offers more map at the same width. So each
   threshold is the width at which its layout first clears seventeen cells, and both fall out of
   the chrome each one costs rather than being picked. Trim a dock and the boundary moves with it.

   `viewport.test.js` pins the derivation, including the ordering that keeps the two ranges from
   crossing. */

// theme.css: --dock-left and --dock-right, both at their clamp() floors, which is where they sit
// at every width that matters here.
export const DESKTOP_DOCKS_PX = 340 + 350
// The tablet layout's chrome: the rail, plus the one dock that stays.
export const TABLET_RAIL_PX = 56
export const TABLET_DOCK_PX = 280
export const TABLET_CHROME_PX = TABLET_RAIL_PX + TABLET_DOCK_PX

// Canvas.js `initialSquareSize`.
export const CELL_PX = 24
// The floor: under this a layout is a porthole, and MobileGate refuses rather than showing it.
export const MIN_MAP_CELLS = 12
// The bar a layout clears before it is preferred to the roomier one below it.
export const COMFORTABLE_MAP_CELLS = 17

export const DESKTOP_MIN_PX = DESKTOP_DOCKS_PX + COMFORTABLE_MAP_CELLS * CELL_PX // 1098
export const TABLET_MIN_PX = TABLET_CHROME_PX + COMFORTABLE_MAP_CELLS * CELL_PX // 744
// Below this even the phone shell — which spends nothing on chrome — has no map worth drawing.
export const USABLE_MIN_PX = MIN_MAP_CELLS * CELL_PX // 288

export const PHONE = "phone"
export const TABLET = "tablet"
export const DESKTOP = "desktop"

/* A phone held sideways. Refused outright — see MobileGate — so this is the query that decides
   what counts as one.

   Three conditions, and each rules out something the other two let through. A **coarse pointer**
   is what says this is a device somebody turns rather than a window somebody drags: a desktop at
   1200x500 is landscape and short and perfectly fine. **Landscape** is the state being refused.
   And the **height floor is the tablet's own width floor**, reused rather than invented: in
   landscape the height is the device's short side, so "its short side is under what a tablet
   needs" is exactly "it is a phone". An iPad at 1024x768 keeps 768 and plays; every iPhone in
   landscape has 320 to 430 and does not. */
export const LANDSCAPE_PHONE_QUERY =
  `(pointer: coarse) and (orientation: landscape) and (max-height: ${TABLET_MIN_PX - 1}px)`

export const TABLET_QUERY = `(min-width: ${TABLET_MIN_PX}px)`
export const DESKTOP_QUERY = `(min-width: ${DESKTOP_MIN_PX}px)`
export const USABLE_QUERY = `(min-width: ${USABLE_MIN_PX}px)`

export const viewportFor = (width) => {
  if (width >= DESKTOP_MIN_PX) return DESKTOP
  if (width >= TABLET_MIN_PX) return TABLET
  return PHONE
}

const matches = (query) => typeof window.matchMedia === "function" && window.matchMedia(query).matches

/* jsdom has no matchMedia, and neither does a server render. Both answer DESKTOP: it is the
   layout the app had before any of this existed, so a test that does not opt in sees what it
   always saw. */
const read = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return DESKTOP
  if (matches(DESKTOP_QUERY)) return DESKTOP
  if (matches(TABLET_QUERY)) return TABLET
  return PHONE
}

export const useViewport = () => {
  const [viewport, setViewport] = useState(read)

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined

    // Both boundaries are watched, not just the one currently straddled: a window dragged from
    // desktop width to phone width in one motion crosses both, and a listener on one of them
    // reports a layout that is a rung out of date.
    const queries = [window.matchMedia(TABLET_QUERY), window.matchMedia(DESKTOP_QUERY)]
    const onChange = () => setViewport(read())
    queries.forEach((query) => query.addEventListener("change", onChange))
    // Between the first read and this effect the window may already have moved.
    onChange()

    return () => queries.forEach((query) => query.removeEventListener("change", onChange))
  }, [])

  return viewport
}

export const useIsPhone = () => useViewport() === PHONE
export const useIsTablet = () => useViewport() === TABLET
// Everything narrower than the desktop docks, which is every layout that is touch-shaped.
export const useIsCompact = () => useViewport() !== DESKTOP

/* The BaseLayout shell each viewport asks for. Two vocabularies on purpose: a viewport is what the
   window *is* (phone / tablet / desktop) and a shell is what the layout *does* with it (sheets /
   a rail plus one dock / two docks). They are not the same word because a phone in landscape asks
   for the compact shell without being a tablet, and calling that "tablet" in the markup would
   make the class name a lie the next reader has to check. */
export const SHELL_BY_VIEWPORT = { [PHONE]: "phone", [TABLET]: "compact", [DESKTOP]: "desktop" }
export const shellFor = (viewport) => SHELL_BY_VIEWPORT[viewport] ?? "desktop"

/* Publishes the shell on the document element, as `data-shell`.

   CSS has to know which shell is running — a modal anchored to the desktop docks is in the wrong
   place under a rail, and off the screen on a phone. It could read the widths back out of a media
   query, but then every stylesheet carries its own copy of a breakpoint that is derived here, and
   a copy of a number is what this codebase keeps finding bugs in. One attribute, and every sheet
   keys off `:root[data-shell="phone"]` instead of restating 743.

   It also reaches places a class on <main> cannot: the modal host renders outside the layout, and
   the modals are exactly what needs to know. */
export const useShellAttribute = () => {
  const viewport = useViewport()

  /* A layout effect rather than an ordinary one: until this attribute lands none of the phone
     rules apply, so an ordinary effect leaves one painted frame laid out as a desktop — on the
     replay that frame is a 300px sidebar beside a 90px sliver of map. A layout effect runs before
     paint, and before every passive effect, so nothing renders or measures itself against a shell
     that is about to be replaced. */
  useLayoutEffect(() => {
    const shell = shellFor(viewport)
    document.documentElement.dataset.shell = shell
    return () => {
      delete document.documentElement.dataset.shell
    }
  }, [viewport])

  return viewport
}
